import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  enterpriseDesignSystemApi,
  type AIExperiencePattern,
  type AccessibilityRequirement,
  type ComponentLibraryEntry,
  type DashboardPhilosophyRule,
  type DataVizStandard,
  type DesignToken,
  type EDSDashboard,
  type EDSBibleStatus,
  type ImplementationStandard,
  type MissionAttribute,
  type MobilePriority,
  type MotionRule,
  type NavigationPattern,
  type PhilosophyPrinciple,
  type ScreenComplianceValidation,
  type VisualIdentityRule,
  type WorkspaceRegion,
} from '../../api/enterprise-design-system';

type Tab = 'overview' | 'philosophy' | 'visual' | 'components' | 'workspace' | 'navigation' | 'data' | 'accessibility' | 'status';
type Theme = 'light' | 'dark';

export function EnterpriseDesignSystemPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<EDSDashboard | null>(null);
  const [mission, setMission] = useState<MissionAttribute[]>([]);
  const [philosophy, setPhilosophy] = useState<PhilosophyPrinciple[]>([]);
  const [visualIdentity, setVisualIdentity] = useState<VisualIdentityRule[]>([]);
  const [components, setComponents] = useState<ComponentLibraryEntry[]>([]);
  const [dashboardPhil, setDashboardPhil] = useState<DashboardPhilosophyRule[]>([]);
  const [workspace, setWorkspace] = useState<WorkspaceRegion[]>([]);
  const [navigation, setNavigation] = useState<NavigationPattern[]>([]);
  const [aiExperience, setAiExperience] = useState<AIExperiencePattern[]>([]);
  const [dataViz, setDataViz] = useState<DataVizStandard[]>([]);
  const [accessibility, setAccessibility] = useState<AccessibilityRequirement[]>([]);
  const [motion, setMotion] = useState<MotionRule[]>([]);
  const [mobile, setMobile] = useState<MobilePriority[]>([]);
  const [tokens, setTokens] = useState<DesignToken[]>([]);
  const [implementation, setImplementation] = useState<ImplementationStandard[]>([]);
  const [status, setStatus] = useState<EDSBibleStatus | null>(null);
  const [screenResult, setScreenResult] = useState<ScreenComplianceValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, mis, phil, vis, comp, dphil, ws, nav, ai, dv, acc, mot, mob, tok, impl, st] = await Promise.all([
        enterpriseDesignSystemApi.dashboard(),
        enterpriseDesignSystemApi.mission(),
        enterpriseDesignSystemApi.philosophy(),
        enterpriseDesignSystemApi.visualIdentity(),
        enterpriseDesignSystemApi.components(),
        enterpriseDesignSystemApi.dashboardPhilosophy(),
        enterpriseDesignSystemApi.productWorkspace(),
        enterpriseDesignSystemApi.navigation(),
        enterpriseDesignSystemApi.aiExperience(),
        enterpriseDesignSystemApi.dataVisualization(),
        enterpriseDesignSystemApi.accessibility(),
        enterpriseDesignSystemApi.motionDesign(),
        enterpriseDesignSystemApi.mobileExperience(),
        enterpriseDesignSystemApi.designTokens(),
        enterpriseDesignSystemApi.implementationStandards(),
        enterpriseDesignSystemApi.status(),
      ]);
      setDashboard(dash);
      setMission(mis);
      setPhilosophy(phil);
      setVisualIdentity(vis);
      setComponents(comp);
      setDashboardPhil(dphil);
      setWorkspace(ws);
      setNavigation(nav);
      setAiExperience(ai);
      setDataViz(dv);
      setAccessibility(acc);
      setMotion(mot);
      setMobile(mob);
      setTokens(tok);
      setImplementation(impl);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load enterprise design system');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateScreen = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await enterpriseDesignSystemApi.validateScreen({
        screen_key: 'asin-workspace',
        attrs: {
          uses_design_tokens: true,
          uses_component_library: true,
          no_custom_patterns: true,
          accessible: true,
          responsive: true,
          dark_mode_support: true,
          action_first: true,
        },
      });
      setScreenResult(result);
      setActionMsg(result.valid ? 'Screen passes EDS-000001 compliance' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-violet-900/50 bg-violet-950/40' : 'border-violet-200 bg-white';
  const muted = dark ? 'text-violet-300/70' : 'text-violet-600/70';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-violet-50/30 text-slate-900';
  const accent = dark ? 'text-violet-300' : 'text-violet-700';
  const tabActive = 'bg-violet-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'philosophy', label: 'Mission & Philosophy' },
    { id: 'visual', label: 'Visual Identity' },
    { id: 'components', label: 'Component Library' },
    { id: 'workspace', label: 'Workspace & Dashboards' },
    { id: 'navigation', label: 'Navigation & AI UX' },
    { id: 'data', label: 'Data & Motion' },
    { id: 'accessibility', label: 'A11y & Mobile' },
    { id: 'status', label: 'Design System Status' },
  ];

  const metricCards = dashboard ? [
    { label: 'Mission Attributes', value: dashboard.mission_attributes },
    { label: 'Components', value: dashboard.components },
    { label: 'Design Tokens', value: dashboard.design_tokens },
    { label: 'Workspace Regions', value: dashboard.workspace_regions },
    { label: 'AI UX Patterns', value: dashboard.ai_experience_patterns },
    { label: 'Data Viz Standards', value: dashboard.data_viz_standards },
    { label: 'Accessibility Rules', value: dashboard.accessibility_rules },
    { label: 'Motion Rules', value: dashboard.motion_rules },
  ] : [];

  const visualByCategory = visualIdentity.reduce<Record<string, VisualIdentityRule[]>>((acc, r) => {
    (acc[r.category] ??= []).push(r);
    return acc;
  }, {});

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>EDS-000001</p>
          <h1 className="text-2xl font-semibold">Enterprise Design System</h1>
          <p className={`text-sm ${muted}`}>
            World-class UI, UX, visual language, component library, interaction system and marketplace operating experience
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/engineering-execution-bible" className={`${accent} hover:underline`}>EEB-000001</Link>
            <span className={muted}>→</span>
            <Link to="/platform/marketplace-mos-bible" className={`${accent} hover:underline`}>MIB-000005 MOS</Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Single Visual Language</h2>
            <p className={`mt-1 text-sm ${muted}`}>
              Version {dashboard.version} · {dashboard.applies_to.join(' · ')}
            </p>
            <p className={`mt-3 text-sm ${muted}`}>
              Software users enjoy opening every morning — modern, premium, fast, minimal, professional, intelligent, AI-native, enterprise-grade, and marketplace-focused.
              Every pixel has a purpose. No screen introduces custom visual patterns outside this standard.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {metricCards.map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'philosophy' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Mission — Design Feel</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {mission.map((m) => (
                <div key={m.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{m.name}</h3>
                  <p className={`mt-2 text-sm ${muted}`}>{m.description}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Design Philosophy</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {philosophy.map((p) => (
                <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs ${p.direction === 'reduce' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'}`}>
                      {p.direction === 'reduce' ? 'Reduce' : 'Increase'}
                    </span>
                    <h3 className="font-semibold">{p.name}</h3>
                  </div>
                  <p className={`mt-2 text-sm ${muted}`}>{p.description}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'visual' && (
        <div className="space-y-6">
          {Object.entries(visualByCategory).map(([category, rules]) => (
            <section key={category}>
              <h2 className="mb-3 text-lg font-semibold capitalize">{category.replace('_', ' ')}</h2>
              <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                {rules.slice(0, category === 'color' ? 12 : rules.length).map((r) => (
                  <div key={r.key} className={`rounded-lg border p-3 ${card}`}>
                    <div className="flex items-center gap-2">
                      {r.category === 'color' && r.value && (
                        <span className="inline-block h-4 w-4 rounded border" style={{ backgroundColor: r.value }} />
                      )}
                      <span className="font-medium text-sm">{r.name}</span>
                    </div>
                    {r.value && r.category !== 'color' && <code className={`mt-1 block text-xs ${muted}`}>{r.value}</code>}
                    {r.description && <p className={`mt-1 text-xs ${muted}`}>{r.description}</p>}
                  </div>
                ))}
              </div>
            </section>
          ))}
          <section>
            <h2 className="mb-3 text-lg font-semibold">Design Tokens ({tokens.length})</h2>
            <div className={`max-h-64 overflow-y-auto rounded-xl border p-3 ${card}`}>
              <div className="grid gap-1 text-xs md:grid-cols-2 lg:grid-cols-3">
                {tokens.slice(0, 30).map((t) => (
                  <div key={t.key} className="flex justify-between gap-2">
                    <span className={muted}>{t.name}</span>
                    <code>{t.value}</code>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'components' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {components.map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <span className="rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900">{c.category}</span>
              <h3 className="mt-2 font-semibold">{c.name}</h3>
              {c.description && <p className={`mt-1 text-sm ${muted}`}>{c.description}</p>}
              {c.react_path && <code className={`mt-2 block text-xs ${muted}`}>{c.react_path}</code>}
              {c.mandatory && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Mandatory</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'workspace' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Dashboard Philosophy — Action First</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {dashboardPhil.map((d) => (
                <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
                  <p className={`text-xs uppercase ${muted}`}>Priority {d.priority}</p>
                  <h3 className="mt-1 font-semibold">{d.question}</h3>
                  <p className={`mt-2 text-sm ${muted}`}>{d.description}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">ASIN Product Workspace</h2>
            <div className="space-y-2">
              {workspace.map((w) => (
                <div key={w.key} className={`flex items-start gap-3 rounded-xl border p-3 ${card}`}>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${w.required ? 'bg-violet-600 text-white' : 'bg-slate-200 dark:bg-slate-700'}`}>
                    {w.order}
                  </span>
                  <div>
                    <h3 className="font-semibold">{w.name}</h3>
                    <p className={`text-sm ${muted}`}>{w.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'navigation' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Navigation</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {navigation.map((n) => (
                <div key={n.key} className={`rounded-xl border p-4 ${card}`}>
                  <span className="rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900">{n.scope}</span>
                  <h3 className="mt-2 font-semibold">{n.name}</h3>
                  <p className={`mt-1 text-sm ${muted}`}>{n.description}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">AI Experience</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {aiExperience.map((a) => (
                <div key={a.key} className={`rounded-xl border p-4 ${card}`}>
                  <span className="rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900">{a.interface_type}</span>
                  <h3 className="mt-2 font-semibold">{a.name}</h3>
                  <p className={`mt-1 text-sm ${muted}`}>{a.description}</p>
                  {a.reasoning_visible && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Reasoning visible</p>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'data' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Data Visualization</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {dataViz.map((d) => (
                <div key={d.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{d.name}</h3>
                  <p className={`text-sm ${muted}`}>{d.description}</p>
                  {d.chart_type && <p className={`mt-1 text-xs ${muted}`}>Type: {d.chart_type}</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Motion Design</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {motion.map((m) => (
                <div key={m.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{m.name}</h3>
                  {m.duration && <p className={`text-xs ${muted}`}>{m.duration}</p>}
                  <p className={`mt-1 text-sm ${muted}`}>{m.description}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Implementation Standards</h2>
            <ul className={`space-y-2 text-sm ${muted}`}>
              {implementation.map((s) => (
                <li key={s.key} className={`rounded-lg border px-3 py-2 ${card}`}>
                  {s.name} — {s.description}
                  {s.required && <span className="ml-2 text-emerald-600 dark:text-emerald-400">Required</span>}
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}

      {tab === 'accessibility' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Accessibility</h2>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {accessibility.map((a) => (
                <div key={a.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{a.name}</h3>
                  {a.standard && <p className={`text-xs ${muted}`}>{a.standard}</p>}
                  {a.mandatory && <p className="mt-1 text-xs text-emerald-600 dark:text-emerald-400">Mandatory</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Mobile Experience Priorities</h2>
            <div className="space-y-2">
              {mobile.map((m) => (
                <div key={m.key} className={`flex items-center gap-3 rounded-xl border p-3 ${card}`}>
                  <span className="rounded-full bg-violet-600 px-2 py-0.5 text-xs text-white">{m.priority}</span>
                  <div>
                    <h3 className="font-semibold">{m.name}</h3>
                    <p className={`text-sm ${muted}`}>{m.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                {status.status}
              </span>
              {status.single_visual_language && (
                <span className={`text-sm ${muted}`}>Single Visual Language Enforced</span>
              )}
            </div>
            <p className={`mt-3 text-sm ${muted}`}>
              Bible {status.bible_id} v{status.version} · {status.components} components · {status.design_tokens} tokens
            </p>
          </div>

          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Screen Compliance</h3>
            <p className={`mt-1 text-sm ${muted}`}>
              Validates a marketplace screen against EDS §14 — design tokens, component library, accessibility, responsive, dark mode, and action-first.
            </p>
            <button
              type="button"
              disabled={busy}
              onClick={validateScreen}
              className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}
            >
              {busy ? 'Validating…' : 'Run Sample Validation'}
            </button>
            {actionMsg && <p className="mt-3 text-sm text-emerald-600 dark:text-emerald-400">{actionMsg}</p>}
            {screenResult && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">
                {JSON.stringify(screenResult, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
